/**
 * GraphBuildDetailView — dedicated page for a single graph build.
 *
 * Route: /admin/creative-review/:clientUid/graph-builds/:buildId
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, CheckCircle2, XCircle, Clock, RefreshCw } from 'lucide-react';
import { clsx } from 'clsx';
import { graphBuildApi, gardenApi, type GraphBuildRun } from '@/services/api';
import { timeAgo } from '@/components/pdo/utils';

const STATUS_BADGE: Record<string, string> = {
  queued: 'bg-slate-100 text-slate-600',
  running: 'bg-blue-50 text-blue-700',
  completed: 'bg-green-50 text-green-700',
  failed: 'bg-red-50 text-red-600',
  cancelled: 'bg-amber-50 text-amber-700',
};

function StatusIcon({ status }: { status: string }) {
  if (status === 'running' || status === 'queued') return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
  if (status === 'completed') return <CheckCircle2 className="w-4 h-4 text-green-500" />;
  if (status === 'failed') return <XCircle className="w-4 h-4 text-red-500" />;
  return <Clock className="w-4 h-4 text-slate-400" />;
}

export function GraphBuildDetailView() {
  const { clientUid = '', buildId = '' } = useParams<{ clientUid: string; buildId: string }>();

  const [build, setBuild] = useState<GraphBuildRun | null>(null);
  const [progress, setProgress] = useState<Record<string, number | null>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [phase2Triggering, setPhase2Triggering] = useState(false);

  const fetchBuild = useCallback(async () => {
    try {
      const data = await graphBuildApi.get(clientUid, buildId);
      setBuild(data);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [clientUid, buildId]);

  const fetchProgress = useCallback(async () => {
    try {
      const resp = await graphBuildApi.getProgress(clientUid, buildId);
      setProgress(resp.tables ?? {});
    } catch { /* non-fatal */ }
  }, [clientUid, buildId]);

  useEffect(() => {
    fetchBuild();
    fetchProgress();
  }, [fetchBuild, fetchProgress]);

  // Poll every 5s when running
  useEffect(() => {
    if (!build || !['running', 'queued'].includes(build.status)) return;
    const iv = setInterval(() => { fetchBuild(); fetchProgress(); }, 5000);
    return () => clearInterval(iv);
  }, [build, fetchBuild, fetchProgress]);

  const cancel = async () => {
    setCancelling(true);
    try {
      await graphBuildApi.cancel(clientUid, buildId);
      await fetchBuild();
    } catch (e) {
      alert(`Cancel failed: ${e}`);
    } finally {
      setCancelling(false);
    }
  };

  const triggerPhase2 = async () => {
    if (!build?.triggered_by_run_id) return;
    setPhase2Triggering(true);
    try {
      await gardenApi.triggerRun(clientUid, {
        run_type: 'creative_review_refresh',
        triggered_by_run_id: build.triggered_by_run_id,
      });
      alert('Phase 2 refresh triggered. Check the Runs tab for progress.');
    } catch (e) {
      alert(`Phase 2 trigger failed: ${e}`);
    } finally {
      setPhase2Triggering(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <Link
        to={`/admin/creative-review/${clientUid}/runs`}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to runs
      </Link>

      {loading && <p className="text-sm text-slate-500">Loading build…</p>}
      {error && <p className="text-sm text-red-600">Error: {error}</p>}

      {build && (
        <div className="space-y-5">
          {/* Header */}
          <header className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <StatusIcon status={build.status} />
                <h1 className="text-xl font-semibold text-slate-900 font-mono">{build.build_id}</h1>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                {clientUid} · env: {build.env}
                {build.started_at && <> · {timeAgo(build.started_at)}</>}
              </p>
              {build.triggered_by_run_id && (
                <p className="text-xs text-slate-400 mt-0.5">
                  Triggered by run:{' '}
                  <Link
                    to={`/admin/creative-review/${clientUid}/runs/${build.triggered_by_run_id}`}
                    className="text-brand-teal underline"
                  >
                    {build.triggered_by_run_id}
                  </Link>
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className={clsx('text-xs px-2 py-1 rounded-full font-medium', STATUS_BADGE[build.status] ?? 'bg-slate-100 text-slate-600')}>
                {build.status}
              </span>
              <button onClick={fetchBuild} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50">
                <RefreshCw className="w-3.5 h-3.5 text-slate-500" />
              </button>
              {['running', 'queued'].includes(build.status) && (
                <button
                  onClick={cancel}
                  disabled={cancelling}
                  className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  {cancelling ? 'Cancelling…' : 'Cancel'}
                </button>
              )}
            </div>
          </header>

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div className="text-xs text-slate-500">PID</div>
              <div className="text-lg font-semibold">{build.pid ?? '—'}</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div className="text-xs text-slate-500">Exit code</div>
              <div className={clsx('text-lg font-semibold', build.exit_code === 0 ? 'text-green-600' : build.exit_code != null ? 'text-red-600' : '')}>
                {build.exit_code ?? '—'}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div className="text-xs text-slate-500">Started</div>
              <div className="text-sm font-medium">{build.started_at ? timeAgo(build.started_at) : '—'}</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div className="text-xs text-slate-500">Completed</div>
              <div className="text-sm font-medium">{build.completed_at ? timeAgo(build.completed_at) : '—'}</div>
            </div>
          </div>

          {/* BQ progress */}
          {Object.keys(progress).length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-4">
              <h3 className="text-xs font-semibold text-slate-600 mb-3">BigQuery row counts</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {Object.entries(progress).map(([table, count]) => (
                  <div key={table} className="rounded-lg bg-slate-50 px-3 py-2">
                    <div className="text-[10px] text-slate-400 font-mono">{table}</div>
                    <div className="text-lg font-semibold text-slate-800">
                      {count != null ? count.toLocaleString() : '—'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {build.error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {build.error}
            </div>
          )}

          {/* Phase 2 CTA */}
          {build.status === 'completed' && build.triggered_by_run_id && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 flex items-center justify-between gap-4">
              <div>
                <div className="text-sm font-semibold text-emerald-800">Build completed!</div>
                <div className="text-xs text-emerald-600 mt-0.5">
                  Run Phase 2 refresh to map raw concepts → resolved clusters and update predictive impact scores.
                </div>
              </div>
              <button
                onClick={triggerPhase2}
                disabled={phase2Triggering}
                className="shrink-0 text-sm px-4 py-2 rounded-lg bg-emerald-600 text-white hover:opacity-90 disabled:opacity-50"
              >
                {phase2Triggering ? 'Triggering…' : 'Run Phase 2 refresh'}
              </button>
            </div>
          )}

          {/* Log tail */}
          {build.log_tail && (
            <div className="rounded-xl border border-slate-200 bg-slate-900 px-4 py-4">
              <h3 className="text-xs font-semibold text-slate-400 mb-2">Log output</h3>
              <pre className="text-[11px] text-green-300 font-mono overflow-x-auto whitespace-pre-wrap max-h-[500px] overflow-y-auto">
                {build.log_tail}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
